/**
 * Phase 28 — Admin Recurring Retest Prompts dashboard.
 *
 * Route: /admin/partners/retest-prompts
 *
 * PINNED USER RULE (Feb 2026):
 *   Phase 28 is preview / admin visibility only. No emails, SMS,
 *   notifications, ServiceM8 jobs, partner messages, customer
 *   reminders, retargeting, calendar tasks or commission /
 *   pricing / white-label / partner-status / public-allocation
 *   changes.
 *   The prominent safety banner reminds admin staff that
 *   `approved_for_future_send` does NOT trigger any dispatch.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, AlertTriangle, RefreshCw, Loader2, ShieldOff,
  CheckCircle2, EyeOff, Clock, FilePlus,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";


const PROMPT_TYPE_LABEL = {
  annual_retest_due:               "Annual retest due",
  post_result_retest_recommended:  "Post-result retest",
  water_passport_renewal_prompt:   "Water Record renewal",
  partner_supported_retest_prompt: "Partner-supported retest",
  routine_quality_check_prompt:    "Routine quality check",
};

const STATUS_LABEL = {
  preview:                  "Preview",
  needs_review:             "Needs review",
  approved_for_future_send: "Approved for future send",
  suppressed:               "Suppressed",
  completed:                "Completed",
  void:                     "Void",
};


function StatusPill({ value, testId }) {
  const tone = {
    preview:                  "bg-sky-50 text-sky-800 border-sky-200",
    needs_review:             "bg-amber-50 text-amber-800 border-amber-200",
    approved_for_future_send: "bg-emerald-50 text-emerald-800 border-emerald-200",
    suppressed:               "bg-slate-100 text-slate-700 border-slate-200",
    completed:                "bg-cyan-50 text-cyan-800 border-cyan-200",
    void:                     "bg-rose-50 text-rose-700 border-rose-200",
  }[value] || "bg-slate-50 text-slate-700 border-slate-200";
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold border ${tone}`}
    >
      {STATUS_LABEL[value] || value}
    </span>
  );
}


export default function PWSRetestPrompts() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ prompt_types: [], prompt_statuses: [] });
  const [summary, setSummary] = useState(null);
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterDueDays, setFilterDueDays] = useState("");
  const [generating, setGenerating] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (filterType) qs.set("prompt_type", filterType);
      if (filterStatus) qs.set("status", filterStatus);
      if (filterDueDays) qs.set("due_within_days", filterDueDays);
      const [a, b] = await Promise.all([
        api.get(`/admin/pws-retest-prompts?${qs.toString()}`),
        api.get(`/admin/pws-retest-prompts/summary`),
      ]);
      setRows(a.data.rows || []);
      setMeta({
        prompt_types: a.data.prompt_types || [],
        prompt_statuses: a.data.prompt_statuses || [],
      });
      setSummary(b.data);
    } catch (err) {
      toast.error(
        formatApiError(err?.response?.data?.detail) ||
          "Failed to load retest prompts",
      );
    } finally {
      setLoading(false);
    }
  }, [filterType, filterStatus, filterDueDays]);

  useEffect(() => { load(); }, [load]);

  const onGenerate = async () => {
    setGenerating(true);
    try {
      const r = await api.post(`/admin/pws-retest-prompts/generate-preview?persist=true&limit=200`);
      toast.success(`Generated ${r.data.previewed} new prompts (preview only)`);
      await load();
    } catch (err) {
      toast.error("Generate preview failed");
    } finally {
      setGenerating(false);
    }
  };

  const updateStatus = async (id, status, note) => {
    try {
      await api.patch(`/admin/pws-retest-prompts/${id}/status`, { status, note });
      toast.success(`Marked as ${STATUS_LABEL[status] || status}`);
      setSelected(null);
      await load();
    } catch (err) {
      toast.error("Status update failed");
    }
  };

  if (loading && rows.length === 0) {
    return (
      <PortalShell>
        <div className="min-h-[40vh] flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-cyan-600 animate-spin" />
        </div>
      </PortalShell>
    );
  }

  return (
    <PortalShell>
      <div
        data-testid="ph28-retest-prompts-page"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-7"
      >
        {/* Breadcrumb + title */}
        <div>
          <Link
            to="/admin/partners"
            className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
          >
            <ArrowLeft className="w-3 h-3" /> Partners
          </Link>
          <h1
            data-testid="ph28-page-title"
            className="font-display mt-2 text-3xl sm:text-4xl font-bold text-slate-900"
          >
            Recurring retest prompts
          </h1>
        </div>

        {/* Prominent safety banner */}
        <div
          data-testid="ph28-safety-banner"
          className="flex items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-5 py-4 text-sm text-amber-900"
        >
          <AlertTriangle className="w-5 h-5 mt-0.5 text-amber-600 flex-shrink-0" />
          <div>
            <div className="font-semibold">
              Retest prompts are preview-only.
            </div>
            <div className="mt-0.5">
              No emails, SMS or reminders are sent automatically from
              this page. Marking a prompt as
              <em> &ldquo;Approved for future send&rdquo;</em> records the
              decision but does NOT dispatch anything.
            </div>
          </div>
        </div>

        {/* Filters + Generate */}
        <div
          data-testid="ph28-filters"
          className="flex flex-wrap items-end gap-3 bg-slate-50 border border-slate-200 rounded-xl p-4"
        >
          <div>
            <label className="text-[11px] font-bold uppercase text-slate-500">
              Prompt type
            </label>
            <select
              data-testid="ph28-filter-type"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="block mt-1 rounded-md border-slate-300 text-sm min-w-[200px]"
            >
              <option value="">All types</option>
              {meta.prompt_types.map((t) => (
                <option key={t} value={t}>{PROMPT_TYPE_LABEL[t] || t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase text-slate-500">
              Status
            </label>
            <select
              data-testid="ph28-filter-status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="block mt-1 rounded-md border-slate-300 text-sm min-w-[160px]"
            >
              <option value="">All statuses</option>
              {meta.prompt_statuses.map((s) => (
                <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase text-slate-500">
              Due within (days)
            </label>
            <input
              data-testid="ph28-filter-due-days"
              type="number"
              min="1" max="730"
              value={filterDueDays}
              placeholder="e.g. 30"
              onChange={(e) => setFilterDueDays(e.target.value)}
              className="block mt-1 rounded-md border-slate-300 text-sm w-[120px]"
            />
          </div>
          <button
            data-testid="ph28-refresh-btn"
            onClick={load}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm bg-slate-900 text-white hover:bg-slate-800"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <button
            data-testid="ph28-generate-preview-btn"
            onClick={onGenerate}
            disabled={generating}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-50"
          >
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FilePlus className="w-3.5 h-3.5" />}
            Generate preview
          </button>
        </div>

        {/* Summary by type */}
        {summary ? (
          <div
            data-testid="ph28-summary"
            className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5"
          >
            {meta.prompt_types.map((t) => {
              const counts = (summary.by_type_status || {})[t] || {};
              const total = Object.values(counts).reduce((s, v) => s + v, 0);
              return (
                <div
                  key={t}
                  data-testid={`ph28-summary-tile-${t}`}
                  className="rounded-xl bg-white border border-slate-200 p-4"
                >
                  <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
                    {PROMPT_TYPE_LABEL[t]}
                  </div>
                  <div className="mt-1.5 font-mono text-2xl font-bold text-slate-900">
                    {total}
                  </div>
                  <div className="mt-1 text-[10px] text-slate-500">
                    {counts.preview || 0} preview · {counts.approved_for_future_send || 0} approved
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}

        {/* Prompts table */}
        <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <h2 className="font-display text-lg font-bold text-slate-900">
              Prompts ({rows.length})
            </h2>
            <span className="text-xs text-slate-500">
              Sorted by suggested due date · oldest first
            </span>
          </div>
          <div className="overflow-x-auto">
            <table
              data-testid="ph28-prompts-table"
              className="min-w-full text-sm"
            >
              <thead className="bg-slate-50">
                <tr>
                  {["Prompt", "Status", "Booking", "Partner",
                    "Suggested due", ""].map((c) => (
                    <th key={c} className="text-left px-4 py-2 text-[11px] font-bold uppercase text-slate-500">
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td
                      data-testid="ph28-prompts-empty"
                      colSpan={6}
                      className="px-4 py-10 text-center text-sm text-slate-500"
                    >
                      No prompts yet. Click <strong>Generate preview</strong>
                      {" "}to scan published tests &amp; completed bookings.
                    </td>
                  </tr>
                ) : rows.map((p) => (
                  <tr
                    key={p.id}
                    data-testid={`ph28-prompts-row-${p.id}`}
                    className="border-t border-slate-100 hover:bg-slate-50/50"
                  >
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-slate-900">
                        {PROMPT_TYPE_LABEL[p.prompt_type] || p.prompt_type}
                      </div>
                      <div className="text-[10px] text-slate-500 font-mono mt-0.5">
                        {p.test_id || "—"} · v{p.prompt_version}
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusPill
                        value={p.status}
                        testId={`ph28-prompt-status-${p.id}`}
                      />
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-slate-700">
                      {p.booking_reference || "—"}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-700">
                      {p.partner_slug ? (
                        <span className="font-mono">{p.partner_slug}</span>
                      ) : <span className="text-slate-400">direct Aware</span>}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-slate-700">
                      {p.suggested_due_at ? p.suggested_due_at.slice(0, 10) : "—"}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        data-testid={`ph28-open-${p.id}`}
                        onClick={() => setSelected(p)}
                        className="text-xs text-cyan-700 hover:text-cyan-900 underline"
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Preview drawer */}
        {selected ? (
          <div
            data-testid="ph28-preview-drawer"
            className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-4"
            onClick={() => setSelected(null)}
          >
            <div
              className="bg-white rounded-3xl max-w-2xl w-full p-6 sm:p-8 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-cyan-600">
                    Preview · admin only
                  </span>
                  <h3 className="font-display text-xl font-bold text-slate-900 mt-1">
                    {PROMPT_TYPE_LABEL[selected.prompt_type] || selected.prompt_type}
                  </h3>
                </div>
                <StatusPill value={selected.status} testId="ph28-drawer-status" />
              </div>
              <div className="space-y-3 text-sm text-slate-700">
                <div className="font-mono text-xs text-slate-500">
                  Booking {selected.booking_reference || "—"} ·
                  Due {selected.suggested_due_at?.slice(0, 10) || "—"} ·
                  {selected.partner_slug || "direct Aware"}
                </div>
                <div
                  data-testid="ph28-preview-body"
                  className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm leading-relaxed"
                >
                  {selected.preview_body || "(no preview text)"}
                </div>
              </div>
              <div
                data-testid="ph28-action-row"
                className="mt-5 flex flex-wrap gap-2 justify-end"
              >
                <button
                  data-testid="ph28-action-needs-review"
                  onClick={() => updateStatus(selected.id, "needs_review")}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100"
                >
                  <Clock className="w-3 h-3" /> Needs review
                </button>
                <button
                  data-testid="ph28-action-approve"
                  onClick={() => updateStatus(selected.id, "approved_for_future_send")}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100"
                >
                  <CheckCircle2 className="w-3 h-3" /> Approve (no dispatch)
                </button>
                <button
                  data-testid="ph28-action-suppress"
                  onClick={() => updateStatus(selected.id, "suppressed")}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200"
                >
                  <EyeOff className="w-3 h-3" /> Suppress
                </button>
                <button
                  data-testid="ph28-action-completed"
                  onClick={() => updateStatus(selected.id, "completed")}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs bg-cyan-50 text-cyan-800 border border-cyan-200 hover:bg-cyan-100"
                >
                  <CheckCircle2 className="w-3 h-3" /> Mark completed
                </button>
                <button
                  data-testid="ph28-action-void"
                  onClick={() => updateStatus(selected.id, "void")}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100"
                >
                  <ShieldOff className="w-3 h-3" /> Void
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </PortalShell>
  );
}
